import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import { discardRecovery, rememberRecovery, readRecovery, restoredSession } from '@/utils/liveClassRecovery';

const LiveClassContext = createContext(null);

export function LiveClassProvider({ children }) {
  const [sessionData, setSessionData] = useState(null);
  // viewMode: 'closed' | 'expanded' | 'minimized'
  const [viewMode, setViewMode] = useState('closed');
  const recoveryGeneration = useRef(0);
  const discardLiveClassRecovery = useCallback(() => {
    recoveryGeneration.current += 1;
    discardRecovery();
  }, []);

  useEffect(() => {
    const marker = readRecovery();
    if (!marker) return undefined;
    const generation = recoveryGeneration.current;
    const controller = new AbortController();
    axios.post(apiUrl('tutor-sessions/livekit-token/'), { booking_id: marker.bookingId }, {
      headers: { Authorization: `Bearer ${getAuthToken(marker.portal)}` }, signal: controller.signal,
    }).then(({ data }) => {
      const current = readRecovery();
      if (controller.signal.aborted || generation !== recoveryGeneration.current ||
          !current || current.bookingId !== marker.bookingId || current.userId !== marker.userId) return;
      const restored = restoredSession(data);
      setSessionData(restored);
      setViewMode('expanded');
    }).catch((error) => {
      if (!controller.signal.aborted && generation === recoveryGeneration.current &&
          (!error.response || [400, 401, 403, 404, 410].includes(error.response.status))) discardRecovery();
    });
    return () => controller.abort();
  }, []);

  // Co-browsing state: 'idle' | 'preparing' | 'active'
  const [coBrowseStatus, setCoBrowseStatus] = useState('idle');
  const [coBrowseSnapshot, setCoBrowseSnapshot] = useState(null);

  // Reference to publisher function provided by active LiveKit Room
  const publisherRef = React.useRef(null);

  const registerLiveKitPublisher = useCallback((fn) => {
    publisherRef.current = fn;
  }, []);

  const unregisterLiveKitPublisher = useCallback(() => {
    publisherRef.current = null;
  }, []);

  const broadcastCoBrowseAction = useCallback((type, payload = {}) => {
    if (type === 'CO_BROWSE_CURRICULUM') {
      const curr = payload.curriculum || payload;
      setCoBrowseSnapshot((prev) => ({
        ...(prev || {}),
        curriculum: {
          subjectId: String(curr.subjectId || ''),
          subjectName: curr.subjectName || '',
          chapterId: String(curr.chapterId || ''),
          chapterName: curr.chapterName || '',
        },
        view: { showTextbook: false, cardPosition: null },
      }));
    }

    if (!publisherRef.current) {
      console.warn('Cannot broadcast co-browse action: no publisher registered');
      return;
    }
    try {
      const msg = JSON.stringify({
        type,
        startedBy: payload.startedBy || 'tutor',
        timestamp: Date.now(),
        ...payload,
      });
      publisherRef.current(new TextEncoder().encode(msg));
    } catch (err) {
      console.warn('Failed to publish co-browse action:', err);
    }
  }, []);

  const openLiveClass = useCallback((data) => {
    if (data) {
      recoveryGeneration.current += 1;
      rememberRecovery(data);
      setSessionData(data);
      setViewMode('expanded');
    }
  }, []);

  const minimizeLiveClass = useCallback(() => {
    setViewMode((prev) => (prev === 'expanded' ? 'minimized' : prev));
  }, []);

  const expandLiveClass = useCallback(() => {
    setViewMode((prev) => (prev === 'minimized' ? 'expanded' : prev));
  }, []);

  const closeLiveClass = useCallback(() => {
    discardLiveClassRecovery();
    setViewMode('closed');
    setSessionData(null);
    setCoBrowseStatus('idle');
    setCoBrowseSnapshot(null);
  }, [discardLiveClassRecovery]);

  useEffect(() => {
    window.addEventListener('sessionLoggedOut', closeLiveClass);
    return () => window.removeEventListener('sessionLoggedOut', closeLiveClass);
  }, [closeLiveClass]);

  // Teacher initiates co-browse prep: minimizes classroom and redirects to Teach
  const prepareCoBrowsing = useCallback((navigate) => {
    setCoBrowseStatus('preparing');
    minimizeLiveClass();
    if (typeof navigate === 'function') {
      navigate('/teacher/teach');
    }
  }, [minimizeLiveClass]);

  // Teacher clicks second button to start streaming to student
  const startStudentCoBrowsing = useCallback((initialPayload = {}) => {
    setCoBrowseStatus('active');
    setCoBrowseSnapshot(initialPayload);
    broadcastCoBrowseAction('CO_BROWSE_START', initialPayload);
  }, [broadcastCoBrowseAction]);

  // Teacher stops co-browsing
  const stopCoBrowsing = useCallback(() => {
    setCoBrowseStatus('idle');
    setCoBrowseSnapshot(null);
    broadcastCoBrowseAction('CO_BROWSE_STOP', {});
    expandLiveClass();
  }, [broadcastCoBrowseAction, expandLiveClass]);

  // Handle incoming data message from remote participant
  const handleIncomingCoBrowseMessage = useCallback((data, isTutorRole) => {
    if (!data?.type || !data.type.startsWith('CO_BROWSE_')) return;

    // Security: Only messages originated by 'tutor' are accepted on student side
    if (isTutorRole) {
      // Tutor ignores remote co-browse start/stop commands from student, but accepts interaction updates
      if (data.type === 'CO_BROWSE_STUDENT_QUIZ_UPDATE') {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('budlee_cobrowse_event', { detail: data }));
        }
      }
      return;
    }

    if (data.startedBy && data.startedBy !== 'tutor') {
      console.warn('Ignored co-browse message from non-tutor:', data);
      return;
    }

    if (data.type === 'CO_BROWSE_START') {
      setCoBrowseStatus('active');
      setCoBrowseSnapshot(data);
      minimizeLiveClass();
    } else if (data.type === 'CO_BROWSE_STOP') {
      setCoBrowseStatus('idle');
      setCoBrowseSnapshot(null);
      expandLiveClass();
    } else if (data.type === 'CO_BROWSE_SCREEN_SHARE_ACTIVE') {
      if (data.active) {
        expandLiveClass();
      } else if (coBrowseStatus === 'active') {
        minimizeLiveClass();
      }
    } else if (data.type === 'CO_BROWSE_SNAPSHOT' || data.type === 'CO_BROWSE_CARD_SYNC') {
      setCoBrowseSnapshot((prev) => ({ ...(prev || {}), ...data }));
    } else if (data.type === 'CO_BROWSE_CURRICULUM') {
      const curr = data.curriculum || data;
      setCoBrowseSnapshot((prev) => ({
        ...(prev || {}),
        curriculum: {
          subjectId: String(curr.subjectId || ''),
          subjectName: curr.subjectName || '',
          chapterId: String(curr.chapterId || ''),
          chapterName: curr.chapterName || '',
        },
        view: { showTextbook: false, cardPosition: null },
      }));
    } else if (data.type === 'CO_BROWSE_LEARNING_PACK_OPEN') {
      setCoBrowseSnapshot((prev) => ({
        ...(prev || {}),
        journey: {
          journeyMode: true,
          learningPackActivities: data.activities,
          currentJourneyIndex: data.startIndex || 0,
        },
      }));
    } else if (data.type === 'CO_BROWSE_LEARNING_PACK_CLOSE') {
      setCoBrowseSnapshot((prev) => ({
        ...(prev || {}),
        journey: null,
      }));
    } else if (data.type === 'CO_BROWSE_JOURNEY_INDEX') {
      setCoBrowseSnapshot((prev) => ({
        ...(prev || {}),
        journey: {
          ...(prev?.journey || {}),
          currentJourneyIndex: data.index || 0,
        },
      }));
    }

    // Dispatch global event for listeners (SubjectChapterContext, LearningPath, BudleeContentViewer)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('budlee_cobrowse_event', { detail: data }));
    }
  }, [expandLiveClass, minimizeLiveClass]);

  const isCoBrowsing = coBrowseStatus === 'active';
  const isPreparingCoBrowse = coBrowseStatus === 'preparing';
  const isLiveClassActive = viewMode !== 'closed';

  const studentCurriculum = useMemo(() => {
    if (!sessionData) return null;
    const details = sessionData.sessionDetails || {};
    const boardId = sessionData.student_board_id || details.studentBoardId || details.student_board_id || null;
    const boardName = sessionData.student_board_name || details.studentBoardName || details.student_board_name || details.student_board || '';
    const gradeId = sessionData.student_grade_id || details.studentGradeId || details.student_grade_id || null;
    const gradeName = sessionData.student_grade_name || details.studentGradeName || details.student_grade_name || '';
    if (!boardId && !gradeId) return null;
    return {
      boardId: boardId ? String(boardId) : null,
      boardName: String(boardName || ''),
      gradeId: gradeId ? String(gradeId) : null,
      gradeName: String(gradeName || ''),
    };
  }, [sessionData]);

  const isCurriculumLocked = Boolean(isLiveClassActive && studentCurriculum?.boardId && studentCurriculum?.gradeId);

  const value = useMemo(() => ({
    sessionData,
    viewMode,
    isLiveClassActive,
    isExpanded: viewMode === 'expanded',
    isMinimized: viewMode === 'minimized',
    coBrowseStatus,
    isCoBrowsing,
    isPreparingCoBrowse,
    coBrowseSnapshot,
    studentCurriculum,
    isCurriculumLocked,
    openLiveClass,
    minimizeLiveClass,
    expandLiveClass,
    closeLiveClass,
    discardLiveClassRecovery,
    prepareCoBrowsing,
    startStudentCoBrowsing,
    stopCoBrowsing,
    broadcastCoBrowseAction,
    registerLiveKitPublisher,
    unregisterLiveKitPublisher,
    handleIncomingCoBrowseMessage,
  }), [
    sessionData,
    viewMode,
    isLiveClassActive,
    coBrowseStatus,
    isCoBrowsing,
    isPreparingCoBrowse,
    coBrowseSnapshot,
    studentCurriculum,
    isCurriculumLocked,
    openLiveClass,
    minimizeLiveClass,
    expandLiveClass,
    closeLiveClass,
    discardLiveClassRecovery,
    prepareCoBrowsing,
    startStudentCoBrowsing,
    stopCoBrowsing,
    broadcastCoBrowseAction,
    registerLiveKitPublisher,
    unregisterLiveKitPublisher,
    handleIncomingCoBrowseMessage,
  ]);

  return (
    <LiveClassContext.Provider value={value}>
      {children}
    </LiveClassContext.Provider>
  );
}

export function useLiveClass() {
  const context = useContext(LiveClassContext);
  if (!context) {
    throw new Error('useLiveClass must be used within a LiveClassProvider');
  }
  return context;
}
